// ABOUTME: React hook for managing multiple Google Calendar accounts.
// ABOUTME: Provides account list, add/remove account functionality, and loading states.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';

// Channel the OAuth callback page uses to hand its result back to this window.
// Must match RESULT_STORAGE_KEY in api/google/auth/callback.js.
const OAUTH_RESULT_KEY = 'zw_google_oauth_result';

export const useGoogleAccounts = () => {
  const { isAuthenticated, token, loading: authLoading, getToken } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccounts = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const authToken = await getToken() || token;
      const response = await fetch('/api/google/accounts', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch accounts');
      }

      setAccounts(data.accounts || []);
    } catch (err) {
      console.error('Error fetching Google accounts:', err);
      setError(err.message);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, getToken]);

  const addAccount = async () => {
    try {
      const authToken = await getToken() || token;

      const response = await fetch('/api/google/auth/start', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start authorization');
      }

      // Clear any stale result from a previous attempt before opening.
      try { window.localStorage.removeItem(OAUTH_RESULT_KEY); } catch (e) {}

      // Open OAuth popup
      const popup = window.open(
        data.authUrl,
        'google-oauth',
        'width=600,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup was blocked. Please allow popups and try again.');
      }

      // The callback page reports its result via TWO channels: postMessage
      // (fast path) and localStorage (reliable path). Google's OAuth pages and
      // our own server set Cross-Origin-Opener-Policy, which can sever the
      // popup's window.opener reference and break postMessage — so localStorage
      // is what actually makes the flow complete. We listen to both and process
      // whichever arrives first.
      return new Promise((resolve, reject) => {
        let settled = false;
        let checkClosed;

        const cleanup = () => {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          window.removeEventListener('storage', storageListener);
          try { window.localStorage.removeItem(OAUTH_RESULT_KEY); } catch (e) {}
        };

        const handleResult = async (result) => {
          if (settled || !result || !result.type) return;

          if (result.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
            settled = true;
            cleanup();
            if (!popup.closed) popup.close();

            try {
              const exchangeResponse = await fetch('/api/google/auth/exchange', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code: result.code })
              });

              const exchangeData = await exchangeResponse.json();

              if (!exchangeResponse.ok) {
                throw new Error(exchangeData.error || 'Failed to connect account');
              }

              await fetchAccounts();
              resolve(exchangeData);
            } catch (err) {
              reject(err);
            }
          } else if (result.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
            settled = true;
            cleanup();
            if (!popup.closed) popup.close();
            reject(new Error(result.error || 'Authorization failed'));
          }
        };

        const messageListener = (event) => {
          if (event.origin !== window.location.origin) return;
          handleResult(event.data);
        };

        const storageListener = (event) => {
          if (event.key !== OAUTH_RESULT_KEY || !event.newValue) return;
          try { handleResult(JSON.parse(event.newValue)); } catch (e) {}
        };

        window.addEventListener('message', messageListener);
        window.addEventListener('storage', storageListener);

        checkClosed = setInterval(() => {
          if (settled) return;
          if (popup.closed) {
            // The popup may have written its result and closed before the
            // storage event reached us — read it directly as a fallback.
            let stored = null;
            try { stored = window.localStorage.getItem(OAUTH_RESULT_KEY); } catch (e) {}
            if (stored) {
              try { handleResult(JSON.parse(stored)); return; } catch (e) {}
            }
            settled = true;
            cleanup();
            fetchAccounts();
            resolve(true);
          }
        }, 1000);

        setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          if (!popup.closed) popup.close();
          reject(new Error('Authorization timeout'));
        }, 5 * 60 * 1000);
      });
    } catch (err) {
      console.error('Error adding Google account:', err);
      throw err;
    }
  };

  const disconnectAccount = async (tokenId) => {
    try {
      const authToken = await getToken() || token;

      const response = await fetch(`/api/google/accounts/${tokenId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect account');
      }

      await fetchAccounts();
      return data;
    } catch (err) {
      console.error('Error disconnecting Google account:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchAccounts();
    }
  }, [authLoading, isAuthenticated, fetchAccounts]);

  return {
    accounts,
    loading,
    error,
    addAccount,
    disconnectAccount,
    refreshAccounts: fetchAccounts,
    hasAccounts: accounts.length > 0
  };
};
